import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { AlertTriangle, CalendarDays, ClipboardList, FileText, FlaskConical, Gauge, MapPin, Ruler, Save, Table2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { hasPermission } from "../../utils/permissions";

const API = process.env.REACT_APP_API_URL;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const SUB_TABS = [
  { key: "calibracion", label: "Reporte de Calibración", icon: FlaskConical },
];

const COLUMNAS_CALIBRACION = [
  { key: "puntoReferencia", label: "Punto ref.", desc: "Punto de medición" },
  { key: "repeticiones", label: "Rep. 1…n", desc: "Lecturas; el número de repeticiones lo define la plantilla" },
  { key: "promedio", label: "Promedio", desc: "Promedio de las repeticiones" },
  { key: "promedioPatron", label: "Prom. patrón", desc: "Solo en variables que capturan la lectura del patrón" },
  { key: "valorReferencia", label: "Ref. corregida", desc: "Valor de referencia corregido, si se calculó" },
  { key: "error", label: "Error", desc: "Error absoluto del punto" },
  { key: "errorRelativo", label: "Error %", desc: "Error relativo del punto" },
  { key: "incertidumbre", label: "Incert. expandida U", desc: "Incertidumbre expandida y factor k, si se calculó" },
  { key: "tolerancia", label: "Tolerancia", desc: "Tolerancia aplicada, si la plantilla la define" },
  { key: "conforme", label: "Conforme", desc: "Indica si cada punto cumple la tolerancia" },
  { key: "patron", label: "Patrón usado", desc: "Patrón / instrumento con el que se midió el punto" },
  { key: "timestamps", label: "Timestamps", desc: "Fecha y hora de cada repetición" },
  { key: "comentario", label: "Comentario", desc: "Comentario registrado en el punto de medición" },
];

const SECCIONES_CALIBRACION = [
  { key: "metrologo", label: "Datos y firma del metrólogo", desc: "Nombre y firma de quien realizó la calibración" },
  { key: "resultado", label: "Resultado", desc: "Resultado global de la calibración (Conforme, No conforme…)" },
  { key: "criterio", label: "Criterio de aceptación", desc: "Descripción del criterio definido en la plantilla" },
  { key: "plantilla", label: "Plantilla usada", desc: "Nombre y versión de la plantilla de calibración" },
];

const CAMPOS_CRONOGRAMA = [
  { key: "tipo", label: "Tipo", desc: "Tipo de servicio programado" },
  { key: "fechaProgramada", label: "Fecha programada", desc: "Fecha en que se programó el servicio" },
  { key: "responsable", label: "Responsable", desc: "Persona asignada al cronograma" },
  { key: "estado", label: "Estado", desc: "Estado del cronograma" },
  { key: "observacion", label: "Observación", desc: "Observación registrada en el cronograma" },
];

const CAMPOS_GESTION = [
  { key: "estado", label: "Estado", desc: "Estado de la gestión" },
  { key: "responsable", label: "Responsable", desc: "Quien ejecutó la gestión" },
  { key: "fechaInicio", label: "Fecha Inicio", desc: "Fecha y hora de inicio" },
  { key: "fechaCierre", label: "Fecha Cierre", desc: "Fecha y hora de cierre" },
  { key: "observaciones", label: "Observaciones", desc: "Observaciones registradas en la gestión" },
  { key: "rutinasCompletadas", label: "Rutinas Completadas", desc: "Rutinas marcadas como realizadas" },
];

const CAMPOS_CLIENTE = [
  { key: "cliente", label: "Cliente", desc: "Nombre del cliente" },
  { key: "fechaCalibracion", label: "Fecha de Calibración", desc: "Fecha registrada en el formulario de calibración" },
  { key: "direccion", label: "Dirección", desc: "Dirección registrada en el cliente" },
  { key: "fechaExpedicion", label: "Fecha de Expedición", desc: "Fecha en que se finalizó la calibración y se expidió el certificado" },
  { key: "sede", label: "Sede", desc: "Sede donde está el equipo" },
  { key: "area", label: "Área", desc: "Grupo y área donde está el equipo" },
];

const CAMPOS_INSTRUMENTO = [
  { key: "categoria", label: "Categoría", desc: "Categoría del equipo" },
  { key: "marcaModelo", label: "Marca/Modelo", desc: "Marca y modelo del equipo" },
  { key: "serial", label: "Serial", desc: "Serial del equipo" },
  { key: "codigo", label: "Código", desc: "Código del equipo" },
  { key: "rangoMedicion", label: "Rango de medición", desc: "De cada variable medida, según la plantilla de calibración" },
  { key: "resolucion", label: "Resolución (d)", desc: "De cada variable medida, según la plantilla de calibración" },
];

const CAMPOS_PATRONES = [
  { key: "nombre", label: "Nombre", desc: "Nombre del patrón o instrumento" },
  { key: "serial", label: "Serial", desc: "Serial del patrón" },
  { key: "numeroCertificado", label: "N° Certificado", desc: "Número del certificado de calibración del patrón" },
  { key: "laboratorio", label: "Laboratorio", desc: "Laboratorio que certificó el patrón" },
  { key: "fechaCalibracion", label: "Fecha calib.", desc: "Fecha de calibración del patrón" },
  { key: "vencimiento", label: "Vencimiento", desc: "Fecha de vencimiento del certificado del patrón" },
  { key: "trazabilidad", label: "Trazabilidad", desc: "Norma y cadena de trazabilidad" },
  { key: "incertidumbre", label: "Incertidumbre", desc: "Incertidumbre declarada del patrón" },
  { key: "estado", label: "Estado en la calibración", desc: "Vigencia del patrón en la fecha de la calibración" },
];

const GRUPOS_OPCIONES = {
  columnas: COLUMNAS_CALIBRACION,
  secciones: [
    ...SECCIONES_CALIBRACION,
    { key: "cronograma" },
    { key: "gestion" },
    { key: "tablaDatos" },
    { key: "grafica" },
    { key: "observacionesVariable" },
  ],
  camposCronograma: CAMPOS_CRONOGRAMA,
  camposGestion: CAMPOS_GESTION,
  camposCliente: CAMPOS_CLIENTE,
  camposInstrumento: CAMPOS_INSTRUMENTO,
  camposPatrones: CAMPOS_PATRONES,
};

// Encabezado de un bloque de opciones, con interruptor si se puede ocultar completo
const EncabezadoBloque = ({ icon: Icon, titulo, desc, activo, puedeEditar, onToggle }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-blue-50 rounded-lg">
      <Icon className="h-5 w-5 text-blue-600" />
    </div>
    <div className="flex-1">
      <h3 className="text-base font-semibold text-gray-900">{titulo}</h3>
      <p className="text-sm text-gray-500">{desc}</p>
    </div>
    {onToggle && (
      <label
        className={`flex items-center gap-2 text-sm font-medium text-gray-700 whitespace-nowrap ${
          puedeEditar ? "cursor-pointer" : "opacity-70 cursor-not-allowed"
        }`}
      >
        <input
          type="checkbox"
          className="h-4 w-4 accent-blue-600"
          checked={!!activo}
          disabled={!puedeEditar}
          onChange={(e) => onToggle(e.target.checked)}
        />
        Incluir en el reporte
      </label>
    )}
  </div>
);

// Grupo de casillas (columnas o secciones)
const OpcionesCheck = ({ opciones, valores, puedeEditar, onChange }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {opciones.map(({ key, label, desc }) => (
      <label
        key={key}
        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
          valores[key] ? "border-blue-300 bg-blue-50/50" : "border-gray-200 bg-white"
        } ${puedeEditar ? "cursor-pointer hover:border-blue-400" : "opacity-70 cursor-not-allowed"}`}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-blue-600"
          checked={!!valores[key]}
          disabled={!puedeEditar}
          onChange={(e) => onChange(key, e.target.checked)}
        />
        <span>
          <span className="block text-sm font-medium text-gray-900">{label}</span>
          <span className="block text-xs text-gray-500">{desc}</span>
        </span>
      </label>
    ))}
  </div>
);

// ─── Reporte de calibración: columnas de las tablas y secciones del reporte ─
const ReporteCalibracionConfig = () => {
  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const puedeEditar = hasPermission("categorias.editar");

  useEffect(() => {
    axios
      .get(`${API}/reporte-config`, auth())
      .then(({ data }) => {
        setConfig(data.calibracion);
        setOriginal(data.calibracion);
      })
      .catch(() => toast.error("No se pudo cargar la configuración de reportes"));
  }, []);

  if (!config) {
    return <p className="text-sm text-gray-500">Cargando configuración…</p>;
  }

  const { columnas, secciones, camposCronograma, camposGestion, camposCliente, camposInstrumento, camposPatrones } = config;
  const sinCambios = Object.entries(GRUPOS_OPCIONES).every(([grupo, opciones]) =>
    opciones.every(({ key }) => config[grupo][key] === original[grupo][key]),
  );

  const cambiar = (grupo) => (key, valor) =>
    setConfig((c) => ({ ...c, [grupo]: { ...c[grupo], [key]: valor } }));
  const cambiarSeccion = (key) => (valor) => cambiar("secciones")(key, valor);

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const { data } = await axios.put(
        `${API}/reporte-config`,
        { calibracion: { columnas, secciones, camposCronograma, camposGestion, camposCliente, camposInstrumento, camposPatrones } },
        auth(),
      );
      setConfig(data.calibracion);
      setOriginal(data.calibracion);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar la configuración");
    } finally {
      setGuardando(false);
    }
  };

  const visibles = COLUMNAS_CALIBRACION.filter(({ key }) => columnas[key]).map(({ label }) => label);
  // Un certificado sin estos datos deja de mostrar el resultado de la medición
  const faltanDatosMetrologicos =
    !secciones.tablaDatos || !columnas.puntoReferencia || !columnas.error || !columnas.incertidumbre;

  return (
    <div className="space-y-5">
      <EncabezadoBloque
        icon={MapPin}
        titulo="Datos del cliente y ubicación"
        desc="Elige qué datos aparecen en esta sección. Si desactivas todos, la sección no aparece en el reporte."
      />

      <OpcionesCheck
        opciones={CAMPOS_CLIENTE}
        valores={camposCliente}
        puedeEditar={puedeEditar}
        onChange={cambiar("camposCliente")}
      />

      <div className="pt-2 border-t border-gray-100">
        <EncabezadoBloque
          icon={Gauge}
          titulo="Datos del instrumento bajo prueba"
          desc="Elige qué datos aparecen en esta sección. Si desactivas todos, la sección no aparece en el reporte."
        />
      </div>

      <OpcionesCheck
        opciones={CAMPOS_INSTRUMENTO}
        valores={camposInstrumento}
        puedeEditar={puedeEditar}
        onChange={cambiar("camposInstrumento")}
      />

      <div className="pt-2 border-t border-gray-100">
        <EncabezadoBloque
          icon={CalendarDays}
          titulo="Cronograma"
          desc="Elige qué datos del cronograma aparecen en el reporte de calibración."
          activo={secciones.cronograma}
          puedeEditar={puedeEditar}
          onToggle={cambiarSeccion("cronograma")}
        />
      </div>

      {secciones.cronograma && (
        <OpcionesCheck
          opciones={CAMPOS_CRONOGRAMA}
          valores={camposCronograma}
          puedeEditar={puedeEditar}
          onChange={cambiar("camposCronograma")}
        />
      )}

      <div className="pt-2 border-t border-gray-100">
        <EncabezadoBloque
          icon={ClipboardList}
          titulo="Gestión"
          desc="Elige qué datos de la gestión aparecen en el reporte de calibración."
          activo={secciones.gestion}
          puedeEditar={puedeEditar}
          onToggle={cambiarSeccion("gestion")}
        />
      </div>

      {secciones.gestion && (
        <OpcionesCheck
          opciones={CAMPOS_GESTION}
          valores={camposGestion}
          puedeEditar={puedeEditar}
          onChange={cambiar("camposGestion")}
        />
      )}

      <div className="pt-2 border-t border-gray-100">
        <EncabezadoBloque
          icon={Ruler}
          titulo="Patrones / Instrumentos de medición utilizados"
          desc="Elige qué columnas aparecen en esta tabla. Si desactivas todas, la tabla no aparece en el reporte."
        />
      </div>

      <OpcionesCheck
        opciones={CAMPOS_PATRONES}
        valores={camposPatrones}
        puedeEditar={puedeEditar}
        onChange={cambiar("camposPatrones")}
      />

      <div className="pt-2 border-t border-gray-100">
        <EncabezadoBloque
          icon={Table2}
          titulo="Tabla de datos de las variables"
          desc={'Elige qué columnas aparecen en la sección "Resultados de medición". Aplica a todos los reportes de calibración que genere tu empresa.'}
          activo={secciones.tablaDatos}
          puedeEditar={puedeEditar}
          onToggle={cambiarSeccion("tablaDatos")}
        />
      </div>

      {secciones.tablaDatos && (
        <>
          <OpcionesCheck
            opciones={COLUMNAS_CALIBRACION}
            valores={columnas}
            puedeEditar={puedeEditar}
            onChange={cambiar("columnas")}
          />

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Vista previa del encabezado
            </p>
            {visibles.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="text-xs border-collapse">
                  <thead>
                    <tr>
                      {visibles.map((c) => (
                        <th
                          key={c}
                          className="bg-blue-700 text-white font-semibold px-3 py-1.5 border border-blue-700 whitespace-nowrap"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Sin columnas seleccionadas: la tabla no aparecerá en el reporte.
              </p>
            )}
          </div>
        </>
      )}

      {faltanDatosMetrologicos && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Estás ocultando el punto de medición, el error o la incertidumbre. El documento se
            seguirá titulando "Certificado de calibración" aunque no los muestre; ISO/IEC 17025
            exige declararlos en el certificado.
          </span>
        </div>
      )}

      <div className="pt-2 border-t border-gray-100">
        <EncabezadoBloque
          icon={FileText}
          titulo="Secciones del reporte"
          desc="Elige qué datos se incluyen en el reporte de calibración. El número de certificado y el sello de resultados siempre se muestran."
        />
      </div>

      <OpcionesCheck
        opciones={[
          { key: "grafica", label: "Gráfica de la variable", desc: "Gráfica de error ± incertidumbre de cada variable" },
          { key: "observacionesVariable", label: "Observaciones de la variable", desc: "Observaciones definidas para cada variable en la plantilla" },
          ...SECCIONES_CALIBRACION,
        ]}
        valores={secciones}
        puedeEditar={puedeEditar}
        onChange={cambiar("secciones")}
      />

      {puedeEditar && (
        <div className="flex justify-end">
          <Button onClick={handleGuardar} disabled={sinCambios || guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      )}
    </div>
  );
};

const ConfigReportesPage = () => {
  const [tab, setTab] = useState(SUB_TABS[0].key);

  return (
    <div className="space-y-5">
      <div className="flex gap-1 border-b border-gray-200">
        {SUB_TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                active
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      {tab === "calibracion" && <ReporteCalibracionConfig />}
    </div>
  );
};

export default ConfigReportesPage;
